"use client";
// Runs a contour canvas: DPR sizing, visibility pause, reduced motion.
import { useEffect, type RefObject } from "react";
import { CONTOUR_STYLES, drawContours, type ContourColors, type ContourVariant } from "./contours";
import { readToken, subscribeTheme } from "./theme";
import { useReducedMotion } from "./useReducedMotion";

// Phase the still frame (and the animation) starts from.
const START_T_S = 4;

export function useContourField(canvasRef: RefObject<HTMLCanvasElement | null>, variant: ContourVariant = "band"): void {
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    let w = 0;
    let h = 0;
    let t = START_T_S;
    const style = CONTOUR_STYLES[variant];
    // Read on every frame: the page variant follows the theme switch.
    const readColors = (): ContourColors => ({ line: readToken(style.lineToken), accent: readToken(style.accentToken) });
    const draw = () => drawContours(ctx, w, h, t, readColors(), style.origin);
    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw();
    };
    const ro = new ResizeObserver(fit);
    ro.observe(canvas);
    fit();
    // Still frame (reduced motion) must still follow theme changes.
    const unsubTheme = subscribeTheme(draw);
    if (reduced) {
      return () => {
        ro.disconnect();
        unsubTheme();
      };
    }

    let visible = false;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(canvas);
    const t0 = performance.now();
    let raf = requestAnimationFrame(function loop(now) {
      raf = requestAnimationFrame(loop);
      if (!visible || document.hidden) return;
      t = (now - t0) / 1000 + START_T_S;
      draw();
    });

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      unsubTheme();
    };
  }, [canvasRef, reduced, variant]);
}
